// src/components/TipTapEditor.js
import React, { useEffect, useCallback, forwardRef, useImperativeHandle, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Underline from "@tiptap/extension-underline";
import TextAlign from '@tiptap/extension-text-align';
import { CustomImage } from "./CustomImage";
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import CodeBlock from '@tiptap/extension-code-block';
import HorizontalRule from '@tiptap/extension-horizontal-rule';

import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Strikethrough,
  Code as CodeIcon,
  Heading1,
  Heading2,
  Heading3,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Quote,
  Image as ImageIcon,
  Link as LinkIcon,
  RotateCcw,
  RotateCw,
  X,
  Tag,
  Folder,
  Star,
  ImagePlus,
  FileText,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Minus,
  Table as TableIcon,
  Code2,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  LayoutTemplate,
  Monitor,
  Tablet,
  Smartphone,
} from "lucide-react";

// Available categories
const CATEGORIES = [
  { id: 'anime', label: 'Anime', color: '#ef4444' },
  { id: 'manga', label: 'Manga', color: '#3b82f6' },
  { id: 'movie', label: 'Movie', color: '#8b5cf6' },
  { id: 'tv', label: 'TV Show', color: '#06b6d4' },
  { id: 'gaming', label: 'Gaming', color: '#22c55e' },
  { id: 'news', label: 'News', color: '#f59e0b' },
  { id: 'review', label: 'Review', color: '#ec4899' },
  { id: 'feature', label: 'Feature', color: '#14b8a6' },
];

/**
 * TipTapEditor
 * Props:
 *  - initialContent: string HTML
 *  - initialTitle: string
 *  - initialExcerpt: string
 *  - initialFeaturedImage: string
 *  - initialTags: string[]
 *  - initialIsFeatured: boolean
 *  - initialCategory: string
 *  - onUpdate: function({ content, title, excerpt, featuredImage, tags, isFeatured, category }) => called whenever anything changes
 */
const TipTapEditor = forwardRef(({
  initialContent = "<p></p>",
  initialTitle = "",
  initialExcerpt = "",
  initialFeaturedImage = "",
  initialTags = [],
  initialIsFeatured = false,
  initialCategory = "",
  onUpdate
}, ref) => {
  // Form state
  const [title, setTitle] = useState(initialTitle);
  const [excerpt, setExcerpt] = useState(initialExcerpt);
  const [featuredImage, setFeaturedImage] = useState(initialFeaturedImage);
  const [tags, setTags] = useState(initialTags);
  const [tagInput, setTagInput] = useState("");
  const [isFeatured, setIsFeatured] = useState(initialIsFeatured);
  const [category, setCategory] = useState(initialCategory);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showTypographyPanel, setShowTypographyPanel] = useState(false);
  const [viewportMode, setViewportMode] = useState('desktop');

  // Typography panel state
  const [fontFamily, setFontFamily] = useState('inherit');
  const [fontSize, setFontSize] = useState('inherit');
  const [fontWeight, setFontWeight] = useState('inherit');
  const [textTransform, setTextTransform] = useState('inherit');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      CustomImage,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: true }),
      Subscript,
      Superscript,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      CodeBlock,
      HorizontalRule,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      const content = editor.getHTML();
      if (onUpdate) {
        onUpdate({ content, title, excerpt, featuredImage, tags, isFeatured, category });
      }
    },
    editorProps: { attributes: { class: "tt-editor-content focus:outline-none" } },
  });

  useImperativeHandle(ref, () => ({
    getHTML: () => editor?.getHTML() || "",
    setContent: (content) => {
      if (editor && typeof content === "string") {
        editor.commands.setContent(content, false);
      }
    },
    getTitle: () => title,
    setTitle: (newTitle) => setTitle(newTitle),
    getExcerpt: () => excerpt,
    setExcerpt: (newExcerpt) => setExcerpt(newExcerpt),
    getFeaturedImage: () => featuredImage,
    setFeaturedImage: (url) => setFeaturedImage(url),
    getTags: () => tags,
    setTags: (newTags) => setTags(newTags),
    getIsFeatured: () => isFeatured,
    setIsFeatured: (value) => setIsFeatured(value),
    getCategory: () => category,
    setCategory: (cat) => setCategory(cat),
  }));

  // Notify parent of all field changes
  const notifyChange = useCallback((updates = {}) => {
    if (onUpdate) {
      onUpdate({
        content: editor?.getHTML() || "",
        title,
        excerpt,
        featuredImage,
        tags,
        isFeatured,
        category,
        ...updates
      });
    }
  }, [editor, title, excerpt, featuredImage, tags, isFeatured, category, onUpdate]);

  // Handle individual field changes
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    notifyChange({ title: e.target.value });
  };

  const handleExcerptChange = (e) => {
    setExcerpt(e.target.value);
    notifyChange({ excerpt: e.target.value });
  };

  const handleFeaturedImageChange = (e) => {
    const url = e.target.value;
    setFeaturedImage(url);
    setImagePreviewError(false);
    notifyChange({ featuredImage: url });
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) {
        const newTags = [...tags, newTag];
        setTags(newTags);
        setTagInput("");
        notifyChange({ tags: newTags });
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = tags.filter(t => t !== tagToRemove);
    setTags(newTags);
    notifyChange({ tags: newTags });
  };

  const handleCategoryChange = (catId) => {
    setCategory(catId);
    notifyChange({ category: catId });
  };

  const handleFeaturedToggle = () => {
    const newValue = !isFeatured;
    setIsFeatured(newValue);
    notifyChange({ isFeatured: newValue });
  };

  const isValidImageUrl = (url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(url) || url.startsWith('http');
  };

  useEffect(() => {
    if (editor && typeof initialContent === "string") {
      const existing = editor.getHTML();
      if (initialContent !== existing) editor.commands.setContent(initialContent, false);
    }
  }, [initialContent, editor]);

  // Sync initial values - run when initial props change (e.g., when editing different articles)
  useEffect(() => {
    setTitle(initialTitle || '');
    setExcerpt(initialExcerpt || '');
    setFeaturedImage(initialFeaturedImage || '');
    setTags(initialTags || []);
    setIsFeatured(initialIsFeatured || false);
    setCategory(initialCategory || '');
    
    // Notify parent of initial values
    if (onUpdate) {
      onUpdate({
        content: initialContent,
        title: initialTitle || '',
        excerpt: initialExcerpt || '',
        featuredImage: initialFeaturedImage || '',
        tags: initialTags || [],
        isFeatured: initialIsFeatured || false,
        category: initialCategory || '',
      });
    }
  }, [initialTitle, initialExcerpt, initialFeaturedImage, initialTags, initialIsFeatured, initialCategory, initialContent, onUpdate]);

  // Image insertion via prompt
  const addImage = useCallback(() => {
    const url = window.prompt("Paste image or GIF URL (direct .jpg/.png/.webp/.gif):");
    if (!url) return;
    const looksLikeImage = /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(url);
    if (!looksLikeImage && !window.confirm("This URL does not look like a direct image. Insert anyway?")) return;
    editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  // Calculate word count and reading time
  const getWordCount = () => {
    if (!editor) return 0;
    const text = editor.getText();
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  };

  const getReadingTime = () => {
    const words = getWordCount();
    const minutes = Math.ceil(words / 200); // Average reading speed
    return minutes;
  };

  // Autosave handler
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (title || editor?.getText()?.trim()) {
        setAutosaveStatus('saved');
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [title, editor]);

  // Table creation handler
  const handleCreateTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setShowTableModal(false);
  };

  // Link modal handlers
  const [linkUrl, setLinkUrl] = useState('');
  const handleInsertLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    }
    setLinkUrl('');
    setShowLinkModal(false);
  };

  // Media library mock images
  const mediaLibraryImages = [
    'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=200&h=120&fit=crop',
    'https://images.unsplash.com/photo-1616530940355-129f4b6c0eab?w=200&h=120&fit=crop',
    'https://images.unsplash.com/photo-1608889825103-eb5ed47b3cdf?w=200&h=120&fit=crop',
    'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=200&h=120&fit=crop',
    'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=200&h=120&fit=crop',
    'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=200&h=120&fit=crop',
    'https://images.unsplash.com/photo-1535016120720-40c6874c3b13?w=200&h=120&fit=crop',
    'https://images.unsplash.com/photo-1607604276583-eef5d076aa5f?w=200&h=120&fit=crop',
  ];

  const [selectedImage, setSelectedImage] = useState(null);
  
  // Internal linking
  const [showInternalLinkModal, setShowInternalLinkModal] = useState(false);
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  
  // Mock articles for internal linking - in production, fetch from API
  const availableArticles = [
    { id: 1, title: 'Top 10 Anime of 2024', slug: 'top-10-anime-2024' },
    { id: 2, title: 'Review: Demon Slayer Season 3', slug: 'demon-slayer-season-3-review' },
    { id: 3, title: 'One Piece Film: Red Analysis', slug: 'one-piece-film-red-analysis' },
    { id: 4, title: 'Best Streaming Services for Anime', slug: 'best-streaming-services-anime' },
    { id: 5, title: 'Manga vs Anime: Which is Better?', slug: 'manga-vs-anime-comparison' },
    { id: 6, title: 'Upcoming Anime Movies 2025', slug: 'upcoming-anime-movies-2025' },
  ];
  
  const filteredArticles = availableArticles.filter(article => 
    article.title.toLowerCase().includes(linkSearchTerm.toLowerCase())
  );

  const handleInsertInternalLink = (article) => {
    // Insert link to internal article
    editor.chain().focus().extendMarkRange("link").setLink({ href: `/article/${article.slug}` }).run();
    setShowInternalLinkModal(false);
    setLinkSearchTerm('');
  };

  const handleInsertImage = (url) => {
    editor.chain().focus().setImage({ src: url }).run();
    setSelectedImage(null);
    setShowMediaLibrary(false);
  };

  // Typography settings handler
  const handleTypographyChange = (type, value) => {
    switch (type) {
      case 'fontFamily':
        setFontFamily(value);
        editor.chain().focus().setFontFamily(value).run();
        break;
      case 'fontSize':
        setFontSize(value);
        editor.chain().focus().setFontSize(value).run();
        break;
      case 'fontWeight':
        setFontWeight(value);
        editor.chain().focus().setFontWeight(value).run();
        break;
      case 'textTransform':
        setTextTransform(value);
        editor.chain().focus().setTextTransform(value).run();
        break;
      default:
        // Handle unknown typography type
        break;
    }
  };

  // Viewport preview styles
  const getViewportStyles = () => {
    switch (viewportMode) {
      case 'desktop':
        return {};
      case 'tablet':
        return { maxWidth: '768px', margin: '0 auto', borderLeft: '1px solid #27272a', borderRight: '1px solid #27272a' };
      case 'mobile':
        return { maxWidth: '375px', margin: '0 auto', borderLeft: '1px solid #27272a', borderRight: '1px solid #27272a' };
      default:
        return {};
    }
  };

  // Show loading state while editor initializes
  if (!editor) {
    return (
      <div className="tt-editor">
        <div className="tt-loading">
          <div className="tt-spinner"></div>
          <span>Loading editor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tt-editor">
      {/* Form Section */}
      <div className="tt-form">
        {/* Title Input */}
        <div className="tt-form-group">
          <label className="tt-label">
            <span>Title</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            placeholder="Enter post title..."
            className="tt-input"
          />
        </div>

        {/* Excerpt Input */}
        <div className="tt-form-group">
          <label className="tt-label">
            <FileText size={16} />
            <span>Excerpt</span>
          </label>
          <textarea
            value={excerpt}
            onChange={handleExcerptChange}
            placeholder="Enter a short summary or excerpt for the article..."
            className="tt-textarea"
            rows={3}
          />
        </div>

        {/* Featured Image Input */}
        <div className="tt-form-group">
          <label className="tt-label">
            <ImagePlus size={16} />
            <span>Featured Image URL</span>
          </label>
          <input
            type="text"
            value={featuredImage}
            onChange={handleFeaturedImageChange}
            placeholder="https://example.com/image.jpg..."
            className="tt-input"
          />
          {/* Image Preview */}
          {featuredImage && (isValidImageUrl(featuredImage) || imagePreviewError) && (
            <div className="tt-image-preview">
              {!imagePreviewError && (
                <img 
                  src={featuredImage} 
                  alt="Featured preview" 
                  onError={() => setImagePreviewError(true)}
                />
              )}
              {imagePreviewError && (
                <div className="tt-image-error">Invalid image URL</div>
              )}
            </div>
          )}
        </div>

        {/* Tags Input */}
        <div className="tt-form-group">
          <label className="tt-label">
            <Tag size={16} />
            <span>Tags</span>
          </label>
          <div className="tt-tags-container">
            <div className="tt-tags-list">
              {tags.map((tag) => (
                <span key={tag} className="tt-tag">
                  {tag}
                  <button 
                    type="button" 
                    onClick={() => handleRemoveTag(tag)}
                    className="tt-tag-remove"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              placeholder="Type and press Enter to add tags..."
              className="tt-tag-input"
            />
          </div>
        </div>

        {/* Category Selection */}
        <div className="tt-form-group">
          <label className="tt-label">
            <Folder size={16} />
            <span>Category</span>
          </label>
          <div className="tt-categories">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => handleCategoryChange(cat.id)}
                className={`tt-category-btn ${category === cat.id ? 'active' : ''}`}
                style={{ 
                  '--cat-color': cat.color,
                  borderColor: category === cat.id ? cat.color : 'transparent'
                }}
              >
                <span 
                  className="tt-category-dot" 
                  style={{ backgroundColor: cat.color }}
                />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Toggle */}
        <div className="tt-form-group tt-form-group-inline">
          <label className="tt-label">
            <Star size={16} />
            <span>Mark as Featured</span>
          </label>
          <button
            type="button"
            onClick={handleFeaturedToggle}
            className={`tt-toggle ${isFeatured ? 'active' : ''}`}
          >
            <span className="tt-toggle-slider" />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="tt-toolbar">
        {/* Text Formatting */}
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="tt-btn-glass"
          title="Bold (Ctrl+B)"
        ><BoldIcon size={18} /></button>
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className="tt-btn-glass"
          title="Italic (Ctrl+I)"
        ><ItalicIcon size={18} /></button>
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className="tt-btn-glass"
          title="Underline (Ctrl+U)"
        ><UnderlineIcon size={18} /></button>
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className="tt-btn-glass"
          title="Strikethrough"
        ><Strikethrough size={18} /></button>
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleCode().run()}
          className="tt-btn-glass"
          title="Inline Code"
        ><CodeIcon size={18} /></button>
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          className="tt-btn-glass"
          title="Subscript"
        ><SubscriptIcon size={18} /></button>
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          className="tt-btn-glass"
          title="Superscript"
        ><SuperscriptIcon size={18} /></button>

        {/* Typography Panel */}
        <button 
          type="button" 
          onClick={() => setShowTypographyPanel(!showTypographyPanel)}
          className={`tt-btn-glass ${showTypographyPanel ? 'active' : ''}`}
          title="Typography"
        ><Type size={18} /></button>

        {/* Headings */}
        <div className="tt-btn-group">
          <button 
            type="button" 
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className="tt-btn-glass"
            title="Heading 1"
          ><Heading1 size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className="tt-btn-glass"
            title="Heading 2"
          ><Heading2 size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className="tt-btn-glass"
            title="Heading 3"
          ><Heading3 size={18} /></button>
        </div>

        {/* Lists */}
        <div className="tt-btn-group">
          <button 
            type="button" 
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className="tt-btn-glass"
            title="Bullet List"
          ><ListIcon size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className="tt-btn-glass"
            title="Ordered List"
          ><ListOrderedIcon size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className="tt-btn-glass"
            title="Blockquote"
          ><Quote size={18} /></button>
        </div>

        {/* Callouts */}
        <button 
          type="button" 
          onClick={() => {
            const calloutHTML = '<div class="tt-callout tt-callout-info"><p>💡 Note: Add your information here...</p></div>';
            editor.chain().focus().insertContent(calloutHTML).run();
          }}
          className="tt-btn-glass"
          title="Insert Callout"
        ><LayoutTemplate size={18} /></button>

        {/* Alignment */}
        <div className="tt-btn-group">
          <button 
            type="button" 
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className="tt-btn-glass"
            title="Align Left"
          ><AlignLeft size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className="tt-btn-glass"
            title="Align Center"
          ><AlignCenter size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className="tt-btn-glass"
            title="Align Right"
          ><AlignRight size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className="tt-btn-glass"
            title="Justify"
          ><AlignJustify size={18} /></button>
        </div>

        {/* Media & Links */}
        <div className="tt-btn-group">
          <button 
            type="button" 
            onClick={() => setShowLinkModal(true)}
            className="tt-btn-glass"
            title="Insert External Link"
          ><LinkIcon size={18} /></button>
          <button 
            type="button" 
            onClick={() => setShowInternalLinkModal(true)}
            className="tt-btn-glass"
            title="Link to Internal Article"
          ><FileText size={18} /></button>
          <button 
            type="button" 
            onClick={() => setShowMediaLibrary(true)}
            className="tt-btn-glass"
            title="Media Library"
          ><ImageIcon size={18} /></button>
          <button 
            type="button" 
            onClick={addImage}
            className="tt-btn-glass"
            title="Add Image via URL"
          ><ImagePlus size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="tt-btn-glass"
            title="Horizontal Rule"
          ><Minus size={18} /></button>
        </div>

        {/* Table */}
        <button 
          type="button" 
          onClick={() => setShowTableModal(true)}
          className="tt-btn-glass"
          title="Insert Table"
        ><TableIcon size={18} /></button>

        {/* Code Block */}
        <button 
          type="button" 
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className="tt-btn-glass"
          title="Code Block"
        ><Code2 size={18} /></button>

        {/* Undo / Redo */}
        <div className="tt-btn-group tt-btn-group-right">
          <button 
            type="button" 
            onClick={() => editor.chain().focus().undo().run()}
            className="tt-btn-glass"
            title="Undo (Ctrl+Z)"
          ><RotateCcw size={18} /></button>
          <button 
            type="button" 
            onClick={() => editor.chain().focus().redo().run()}
            className="tt-btn-glass"
            title="Redo (Ctrl+Y)"
          ><RotateCw size={18} /></button>
        </div>
      </div>

      {/* Editor area */}
      <div className="tt-editor-content" style={getViewportStyles()}>
        <EditorContent editor={editor} />
      </div>

      {/* Status Bar - Word Count & Reading Time */}
      <div className="tt-status-bar">
        <div className="tt-status-item">
          <span>{getWordCount()} words</span>
        </div>
        <div className="tt-status-item">
          <span>{getReadingTime()} min read</span>
        </div>
        <div className={`tt-autosave ${autosaveStatus}`}>
          <span className="tt-autosave-dot"></span>
          <span>{autosaveStatus === 'saved' ? 'Saved' : autosaveStatus === 'saving' ? 'Saving...' : 'Draft'}</span>
        </div>
      </div>

      {/* Viewport Preview Bar */}
      <div className="tt-viewport-bar">
        <button 
          type="button" 
          onClick={() => setViewportMode('desktop')}
          className={`tt-viewport-btn ${viewportMode === 'desktop' ? 'active' : ''}`}
        >
          <Monitor size={14} /> Desktop
        </button>
        <button 
          type="button" 
          onClick={() => setViewportMode('tablet')}
          className={`tt-viewport-btn ${viewportMode === 'tablet' ? 'active' : ''}`}
        >
          <Tablet size={14} /> Tablet
        </button>
        <button 
          type="button" 
          onClick={() => setViewportMode('mobile')}
          className={`tt-viewport-btn ${viewportMode === 'mobile' ? 'active' : ''}`}
        >
          <Smartphone size={14} /> Mobile
        </button>
      </div>

      {/* Typography Panel Dropdown */}
      {showTypographyPanel && (
        <div className="tt-typography-panel open">
          <div className="tt-typography-row">
            <div className="tt-typography-field">
              <label className="tt-modal-label">Font Family</label>
              <select 
                className="tt-typography-select"
                value={fontFamily}
                onChange={(e) => handleTypographyChange('fontFamily', e.target.value)}
              >
                <option value="inherit">Default</option>
                <option value="Inter">Inter</option>
                <option value="Arial">Arial</option>
                <option value="Georgia">Georgia</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
              </select>
            </div>
            <div className="tt-typography-field">
              <label className="tt-modal-label">Size</label>
              <select 
                className="tt-typography-select"
                value={fontSize}
                onChange={(e) => handleTypographyChange('fontSize', e.target.value)}
              >
                <option value="inherit">Default</option>
                <option value="12px">12px</option>
                <option value="14px">14px</option>
                <option value="16px">16px</option>
                <option value="18px">18px</option>
                <option value="20px">20px</option>
                <option value="24px">24px</option>
              </select>
            </div>
          </div>
          <div className="tt-typography-row">
            <div className="tt-typography-field">
              <label className="tt-modal-label">Weight</label>
              <select 
                className="tt-typography-select"
                value={fontWeight}
                onChange={(e) => handleTypographyChange('fontWeight', e.target.value)}
              >
                <option value="inherit">Default</option>
                <option value="400">Normal</option>
                <option value="500">Medium</option>
                <option value="600">Semibold</option>
                <option value="700">Bold</option>
              </select>
            </div>
            <div className="tt-typography-field">
              <label className="tt-modal-label">Transform</label>
              <select 
                className="tt-typography-select"
                value={textTransform}
                onChange={(e) => handleTypographyChange('textTransform', e.target.value)}
              >
                <option value="inherit">Default</option>
                <option value="uppercase">UPPERCASE</option>
                <option value="lowercase">lowercase</option>
                <option value="capitalize">Capitalize</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="tt-modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="tt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tt-modal-header">
              <h3 className="tt-modal-title">Insert Link</h3>
              <button 
                type="button" 
                className="tt-modal-close" 
                onClick={() => setShowLinkModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="tt-modal-label">URL</label>
              <input 
                type="text" 
                className="tt-modal-input"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInsertLink()}
              />
            </div>
            <div className="tt-modal-actions">
              <button 
                type="button" 
                className="tt-modal-btn tt-modal-btn-secondary"
                onClick={() => setShowLinkModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="tt-modal-btn tt-modal-btn-primary"
                onClick={handleInsertLink}
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Modal */}
      {showTableModal && (
        <div className="tt-modal-overlay" onClick={() => setShowTableModal(false)}>
          <div className="tt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tt-modal-header">
              <h3 className="tt-modal-title">Insert Table</h3>
              <button 
                type="button" 
                className="tt-modal-close" 
                onClick={() => setShowTableModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="tt-table-creator">
              <div className="tt-table-input-group">
                <label className="tt-modal-label">Rows</label>
                <input 
                  type="number" 
                  className="tt-modal-input" 
                  defaultValue={3}
                  min={1}
                  max={10}
                  id="tt-table-rows"
                />
              </div>
              <div className="tt-table-input-group">
                <label className="tt-modal-label">Columns</label>
                <input 
                  type="number" 
                  className="tt-modal-input" 
                  defaultValue={3}
                  min={1}
                  max={10}
                  id="tt-table-cols"
                />
              </div>
            </div>
            <div className="tt-modal-actions">
              <button 
                type="button" 
                className="tt-modal-btn tt-modal-btn-secondary"
                onClick={() => setShowTableModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="tt-modal-btn tt-modal-btn-primary"
                onClick={() => {
                  const rows = parseInt(document.getElementById('tt-table-rows')?.value || 3);
                  const cols = parseInt(document.getElementById('tt-table-cols')?.value || 3);
                  handleCreateTable(rows, cols);
                }}
              >
                Create Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="tt-modal-overlay" onClick={() => setShowMediaLibrary(false)}>
          <div className="tt-modal" style={{ minWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="tt-modal-header">
              <h3 className="tt-modal-title">Media Library</h3>
              <button 
                type="button" 
                className="tt-modal-close" 
                onClick={() => setShowMediaLibrary(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="tt-media-grid">
              {mediaLibraryImages.map((url, index) => (
                <div 
                  key={index}
                  className={`tt-media-item ${selectedImage === url ? 'selected' : ''}`}
                  onClick={() => setSelectedImage(url)}
                >
                  <img src={url} alt={`Media ${index + 1}`} />
                </div>
              ))}
            </div>
            <div className="tt-modal-actions">
              <button 
                type="button" 
                className="tt-modal-btn tt-modal-btn-secondary"
                onClick={() => {
                  // Option to add custom URL
                  const url = window.prompt('Or enter a custom image URL:');
                  if (url) handleInsertImage(url);
                }}
              >
                Add Custom URL
              </button>
              <button 
                type="button" 
                className="tt-modal-btn tt-modal-btn-primary"
                disabled={!selectedImage}
                onClick={() => selectedImage && handleInsertImage(selectedImage)}
              >
                Insert Image
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Internal Link Modal */}
      {showInternalLinkModal && (
        <div className="tt-modal-overlay" onClick={() => setShowInternalLinkModal(false)}>
          <div className="tt-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tt-modal-header">
              <h3 className="tt-modal-title">Link to Internal Article</h3>
              <button 
                type="button" 
                className="tt-modal-close" 
                onClick={() => setShowInternalLinkModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ marginBottom: 16 }}>
              <input 
                type="text" 
                className="tt-modal-input"
                placeholder="Search articles..."
                value={linkSearchTerm}
                onChange={(e) => setLinkSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
            <div className="tt-media-grid" style={{ maxHeight: '200px' }}>
              {filteredArticles.length > 0 ? (
                filteredArticles.map((article) => (
                  <div 
                    key={article.id}
                    className="tt-media-item"
                    onClick={() => handleInsertInternalLink(article)}
                    style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <span style={{ fontSize: '12px', color: '#e4e4e7', textAlign: 'center' }}>{article.title}</span>
                  </div>
                ))
              ) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#71717a' }}>
                  No articles found
                </div>
              )}
            </div>
            <div className="tt-modal-actions">
              <button 
                type="button" 
                className="tt-modal-btn tt-modal-btn-secondary"
                onClick={() => setShowInternalLinkModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default TipTapEditor;
